import { NavigationExtras, Router } from '@angular/router';
import { RoutesValues } from '../enums/routes.enum';
import { ContractFolder } from '../../features/contracts/models/contractFolder.model';
import { DocumentRequest } from '../../features/document-requests/models/documentRequest.model';
import { DocumentRequestDetail } from '../../features/document-requests/models/documentRequestDetail.model';
import { Contract } from '../../features/contracts/models/contract.model';
import { ContractViewHistoryData } from '../../routes/contracts/models/contract-view-history-state';
import { ContractFormHistoryData } from '../../routes/contracts/models/contract-form-history-data';
import { ContractEditHistoryData } from '../../routes/contracts/models/contract-edit-history-data';
import { ModelSubfolderData } from '../../routes/models/models/model-subfolder-data';
import { ModelCreationData } from '../../routes/models/models/model-creation-data';
import { ModelTemplateData } from '../../routes/models/models/model-template-data';
import { ModelTemplateCreateData } from '../../routes/models/models/model-template-create-data';
import { ModelTemplateEditData } from '../../routes/models/models/model-template-edit-data';
import { ModelTemplateViewData } from '../../routes/models/models/model-template-view-data';
import { User } from '../../features/user/user.model';
import { Country } from '../../features/country/country.model';
import { DocumentsToGenerate } from '../../features/documents/models/documentToGenerate.model';
import { CountryConfigFileHistoryData } from '../../routes/country-config/country-config.type';
import { DetailsDocumentRequestFrom } from '../utils/utils';

/**
 *
 * This class provides utility functions for navigating between different routes in the application.
 */
export class RouterHelper {
  static navigateWithState = (
    router: Router,
    route: string,
    state?:
      | Record<string, string | object | number | boolean>
      | ContractViewHistoryData,
  ): void => {
    const navigationExtras: NavigationExtras = {
      state: state,
    };
    router.navigate([route], navigationExtras);
  };

  static navigateToNotfoundPage(router: Router, data: Record<string, string>) {
    const route = `/${RoutesValues.NOT_FOUND}`;
    RouterHelper.navigateWithState(router, route, { notFoundData: data });
  }

  static navigateToDashboard(router: Router) {
    const route = `/${RoutesValues.HOME}`;
    RouterHelper.navigateWithState(router, route);
  }

  static navigateToLoginPage(router: Router) {
    const route = `/${RoutesValues.LOGIN}`;
    RouterHelper.navigateWithState(router, route);
  }

  static navigateToGuestContractViewPage(
    contractId: string,
    router: Router,
  ): void {
    const route = `/${RoutesValues.GUESTS}/${RoutesValues.CONTRACTS}/${contractId}/${RoutesValues.VIEW}`;
    RouterHelper.navigateWithState(router, route);
  }

  static navigateToGuestLoginPage(router: Router): void {
    const route = `/${RoutesValues.GUESTS}/${RoutesValues.CONTRACTS}`;
    RouterHelper.navigateWithState(router, route);
  }

  static navigateToContractListPage(router: Router): void {
    const route = `/${RoutesValues.CONTRACTS}`;
    RouterHelper.navigateWithState(router, route);
  }

  static navigateToContractFolderPage(router: Router): void {
    const route = `/${RoutesValues.CONTRACTS}/${RoutesValues.FOLDERS}`;
    RouterHelper.navigateWithState(router, route);
  }

  static navigateToContractDetails(contract: Contract, router: Router): void {
    const route = `/${RoutesValues.CONTRACTS}/${contract._id}`;
    RouterHelper.navigateWithState(router, route, { contract });
  }

  static naviateToContractFolderDetailPage(
    state: {
      contractFolder: ContractFolder;
      createdContract?: Contract;
      shouldAssignCreatedContractLater?: boolean;
    },
    router: Router,
  ): void {
    const route = `/${RoutesValues.CONTRACTS}/${RoutesValues.FOLDERS}/${state.contractFolder._id}`;
    RouterHelper.navigateWithState(router, route, state);
  }

  static navigateToContractViewPage(
    state: ContractViewHistoryData,
    router: Router,
  ): void {
    const route = `/${RoutesValues.CONTRACTS}/${RoutesValues.FOLDERS}/${state.contractFolder._id}/${state.contract._id}/${RoutesValues.VIEW}`;
    RouterHelper.navigateWithState(router, route, state);
  }

  static navigateToContractFormPage(
    state: ContractFormHistoryData,
    router: Router,
  ): void {
    const route = `/${RoutesValues.CONTRACTS}/${RoutesValues.FOLDERS}/${state.contractFolder._id}/${RoutesValues.CREATE}/${RoutesValues.FORM}`;
    RouterHelper.navigateWithState(router, route, { contractCreate: state });
  }

  static navigateToContractEditPage(
    state: ContractEditHistoryData,
    router: Router,
  ): void {
    const route = `/${RoutesValues.CONTRACTS}/${RoutesValues.FOLDERS}/${state.contractFolder._id}/${RoutesValues.CREATE}/${RoutesValues.EDIT_TEMPLATE}`;
    RouterHelper.navigateWithState(router, route, { contractForm: state });
  }

  static navigateToContractViewEditPage(
    state: ContractViewHistoryData,
    router: Router,
  ): void {
    const route = `/${RoutesValues.CONTRACTS}/${RoutesValues.FOLDERS}/${state.contractFolder._id}/${state.contract._id}/${RoutesValues.EDIT}`;
    RouterHelper.navigateWithState(router, route, state);
  }

  static navigateToContractEditIPage(
    state: ContractEditHistoryData | ContractViewHistoryData,
    router: Router,
  ): void {
    const route = `/${RoutesValues.CONTRACTS}/${RoutesValues.FOLDERS}/${state.contractFolder._id}/${RoutesValues.CREATE}/${RoutesValues.EDIT_TEMPLATE}`;
    RouterHelper.navigateWithState(router, route, { contractForm: state });
  }

  static navigateToModelFolderPage(router: Router): void {
    const route = `/${RoutesValues.MODELS}/${RoutesValues.FOLDERS}/`;
    RouterHelper.navigateWithState(router, route);
  }

  static navigateToModelFolderDetailPage(
    router: Router,
    state: ModelSubfolderData,
  ): void {
    const route = `/${RoutesValues.MODELS}/${RoutesValues.FOLDERS}/${state.folder._id}`;
    RouterHelper.navigateWithState(router, route, {
      modelSubfolderData: state,
    });
  }

  static navigateToModelCreationPage(
    router: Router,
    state: ModelCreationData,
  ): void {
    const route = `/${RoutesValues.MODELS}/${RoutesValues.FOLDERS}/${state.folder._id}/${RoutesValues.NEW_MODEL}`;
    RouterHelper.navigateWithState(router, route, { modelCreationData: state });
  }

  static navigateToModelTemplatePage(
    router: Router,
    folderId: string,
    subfolderId: string,
    state: ModelTemplateData,
  ): void {
    const route = `/${RoutesValues.MODELS}/${RoutesValues.FOLDERS}/${folderId}/${RoutesValues.DETAILS}/${subfolderId}/${RoutesValues.TEMPLATES}`;
    RouterHelper.navigateWithState(router, route, { modelTemplateData: state });
  }

  static navigateToModelCreationEditTemplate(
    router: Router,
    folderId: string,
    state: ModelTemplateCreateData,
  ): void {
    const route = `/${RoutesValues.MODELS}/${RoutesValues.FOLDERS}/${folderId}/${RoutesValues.NEW_MODEL}/${RoutesValues.EDIT_TEMPLATE}`;
    RouterHelper.navigateWithState(router, route, {
      modelTemplateCreateData: state,
    });
  }

  static navigateToModelTemplateEditPage(
    router: Router,
    state: ModelTemplateEditData,
  ): void {
    const route = `/${RoutesValues.MODELS}/${RoutesValues.FOLDERS}/${state.parentId}/${RoutesValues.DETAILS}/${state.template._id}/${RoutesValues.EDIT_TEMPLATE}`;
    RouterHelper.navigateWithState(router, route, {
      modelTemplateEditData: state,
    });
  }

  static navigateToModelTemplateViewPage(
    router: Router,
    state: ModelTemplateViewData,
  ): void {
    const route = `/${RoutesValues.MODELS}/${RoutesValues.FOLDERS}/${state.templateEditData.parentId}/${RoutesValues.DETAILS}/${state.template._id}/${RoutesValues.VIEW}`;
    RouterHelper.navigateWithState(router, route, {
      modelTemplateViewData: state,
    });
  }

  static navigateToDocumentRequestListPage(router: Router): void {
    const route = `/${RoutesValues.DOCUMENT_REQUESTS}`;
    RouterHelper.navigateWithState(router, route);
  }

  static navigateToDocumentRequestSendedListPage(router: Router): void {
    const route = `${RoutesValues.EMPLOYEES}/${RoutesValues.DOCUMENT_REQUESTS}`;
    RouterHelper.navigateWithState(router, route);
  }

  static navigateToDocumentRequestCreatePage(
    router: Router,
    affectDocumentRequest: boolean,
  ): void {
    const route = `/${RoutesValues.DOCUMENT_REQUESTS}/${RoutesValues.CREATE}`;
    RouterHelper.navigateWithState(router, route, { affectDocumentRequest });
  }

  static navigateToDocumentRequestViewPage(
    router: Router,
    documentRequestId: string,
    requestType: DocumentRequest | DocumentRequestDetail,
  ) {
    const route = `/${RoutesValues.DOCUMENT_REQUESTS}/${requestType._id}/${RoutesValues.FORM}`;
    RouterHelper.navigateWithState(router, route, {
      data: requestType,
    });
  }

  static navigateToDocumentRequestDetailPage(
    router: Router,
    comingFrom: DetailsDocumentRequestFrom,
    requestType: DocumentRequest | DocumentRequestDetail,
  ) {
    const route = `/${RoutesValues.DOCUMENT_REQUESTS}/${requestType._id}/${RoutesValues.DETAILS}`;
    RouterHelper.navigateWithState(router, route, {
      data: requestType,
      from: comingFrom,
    });
  }

  static navigateToDocumentRequestSignPage(
    router: Router,
    request: DocumentRequestDetail,
  ) {
    const route = `/${RoutesValues.DOCUMENT_REQUESTS}/${request._id}/${RoutesValues.SIGN}`;
    RouterHelper.navigateWithState(router, route, { data: request });
  }

  static navigateToEmployeeListPage(router: Router): void {
    const route = `/${RoutesValues.EMPLOYEES}`;
    RouterHelper.navigateWithState(router, route);
  }

  static navigateToEmployeeDetailPage(router: Router, employee: User) {
    const route = `/${RoutesValues.EMPLOYEES}/${employee._id}/${RoutesValues.DETAILS}`;
    RouterHelper.navigateWithState(router, route, { employee });
  }

  static navigateToProfilePage(router: Router) {
    const route = `/${RoutesValues.PROFILE}`;
    RouterHelper.navigateWithState(router, route);
  }

  static navigateToCountryConfigFiles(
    country: Country,
    data: CountryConfigFileHistoryData,
    router: Router,
  ): void {
    const route = `/${RoutesValues.COUNTRY_CONFIG}/${country.code}/${RoutesValues.FILES}`;
    RouterHelper.navigateWithState(router, route, { data });
  }

  static navigateToCountryConfigFilesEdit(
    country: Country,
    file: DocumentsToGenerate,
    data: CountryConfigFileHistoryData,
    router: Router,
  ): void {
    const route = `/${RoutesValues.COUNTRY_CONFIG}/${country.code}/${RoutesValues.FILES}/${file._id}/${RoutesValues.EDIT}`;
    RouterHelper.navigateWithState(router, route, { data });
  }

  static navigateToCountryConfigFilesCreate(
    country: Country,
    data: CountryConfigFileHistoryData,
    router: Router,
  ): void {
    const route = `/${RoutesValues.COUNTRY_CONFIG}/${country.code}/${RoutesValues.FILES}/${RoutesValues.NEW}/${RoutesValues.EDIT}`;
    RouterHelper.navigateWithState(router, route, { data });
  }

  static navigateToCountryConfigSign(country: Country, router: Router): void {
    const route = `/${RoutesValues.COUNTRY_CONFIG}/${country.code}/${RoutesValues.SIGN}`;
    RouterHelper.navigateWithState(router, route, { data: { country } });
  }
}
